/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext, IModuleContract } from '@msdyn365-commerce/core-internal';
import { PROCESS_TYPE } from '../consts';
import { IProductListMetaData, IServerLink } from './hydrator-action-inputs';
import { hydrateLink } from './link-hydrator';
import { hydrateProductList } from './product-list-hydrator';

const TYPE_KEY = 'type';
const LIST_TYPE_KEY = 'listType';

/**
 * Recusrively traverses a module's config properties to find link/list types to transform
 *
 * @param obj The current object
 * @param context The current action context
 */
const searchForComplexType = (
    obj: unknown,
    context: IActionContext,
    moduleId?: string,
    propertyToMapInConfig?: string
): Promise<void>[] => {
    const promisesArray: Promise<void>[] = [];
    if (!obj) {
        return promisesArray;
    }
    if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
            promisesArray.push(...searchForComplexType(obj[i], context, moduleId, propertyToMapInConfig));
        }
    } else if (typeof obj === 'object' && obj) {
        const children = Object.keys(obj);

        // Link scenario ('type' is defined)
        if (obj[TYPE_KEY]) {
            promisesArray.push(hydrateLink(<IServerLink>obj, context));
            // ProductList scenario ('list_type') is defined
        } else if (obj[LIST_TYPE_KEY]) {
            promisesArray.push(hydrateProductList(<IProductListMetaData>obj, context, moduleId, propertyToMapInConfig));
        } else if (children.length > 0) {
            for (let i = 0; i < children.length; i++) {
                promisesArray.push(...searchForComplexType(obj[children[i]], context, moduleId, children[i]));
            }
        }
    }

    return promisesArray;
};

/**
 * Entry point, processes a page response and looks for link and list types
 * to transform
 *
 * @param pageResponse The inital page response
 * @param context The current action context
 */
export const processComplexDataTypes = async (
    lazyloadAllDataActions: boolean,
    processType: string,
    pageSlots: IModuleContract[],
    context: IActionContext
) => {
    const promisesArray = processComplexDataTypesAsync(lazyloadAllDataActions, processType, pageSlots, context);
    if (promisesArray) {
        await Promise.all(promisesArray);
    }
};

export const processComplexDataTypesAsync = (
    lazyLoadAllDataActions: boolean,
    processType: string,
    pageSlots: IModuleContract[],
    context: IActionContext
) => {
    if (pageSlots && pageSlots.length) {
        const promisesArray: Promise<void>[] = [];
        let lazyLoadAllModuleQueryString; // Query string switch to lazyLoad all modules
        const requestContext = context.requestContext;
        const requestContextFeatures = requestContext.features;
        const clientRenderModules = 'clientRender_modules';
        const clientRenderAllModuleQueryString = 'client_render_all_modules';

        // Feature switch for lazyLoading render and data action currenlty for product collection module
        let lazyLoadModuleFeature = requestContextFeatures[clientRenderModules];

        // Query param to lazyLoad all modules
        if (requestContext.query && requestContext.query.clientRenderAllModuleQueryString) {
            lazyLoadAllModuleQueryString = requestContext.query[clientRenderAllModuleQueryString];
        }

        // If request is coming from the editor then, turn off all lazyloading feature
        if (requestContext.params && requestContext.params.isEditor && requestContext.params.isEditor === true) {
            lazyLoadModuleFeature = false;
            lazyLoadAllModuleQueryString = 'false';
            lazyLoadAllDataActions = false;
        }

        for (let i = 0; i < pageSlots.length; i++) {
            const currModule = pageSlots[i];
            const currModuleConfig = currModule.config;
            let clientRenderConfigSwitch; // Module config switch for lazyLoading render and data action for product collection module

            if (typeof currModuleConfig === 'object' && currModuleConfig) {
                lazyLoadAllModuleQueryString === 'true' ? (currModuleConfig[`clientRender`] = true) : clientRenderConfigSwitch;
                if (currModuleConfig.hasOwnProperty('clientRender')) {
                    // When the lazyLoading feature switch is false, turn the lazyloading config switch to false
                    if (lazyLoadModuleFeature === false) {
                        currModuleConfig[`clientRender`] = false;
                    }
                    clientRenderConfigSwitch = currModuleConfig[`clientRender`];
                }
            }

            if ((lazyLoadAllDataActions || clientRenderConfigSwitch) && processType === PROCESS_TYPE.client) {
                // When lazyLoad_all feature switch is true or lazyload Module config switch is true and processComplexDataType call is from client, do hydration
                promisesArray.push(...searchForComplexType(currModuleConfig, context, currModule.id));
            } else if (!lazyLoadAllDataActions && !clientRenderConfigSwitch && processType === PROCESS_TYPE.server) {
                // When lazyLoad_all feature switch is OFF, lazyload Module config switch is OFF and processComplexDataType call is from server, do hydration
                promisesArray.push(...searchForComplexType(currModuleConfig, context, currModule.id));
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        context.runAllActionsInQueue();
        return promisesArray;
    }
};
